<svelte:options accessors />

<script context="module">
  export const events = {
    componentCreated: 'componentCreated',
  };
</script>

<script lang="ts">
  import { isFunction } from 'lodash-es';
  import { createEventDispatcher, getAllContexts, SvelteComponent } from 'svelte';

  import type { IComponentPropsConfig } from '@/svelte-comp-adapter/interface';

  import { resolveSvelteComponent } from '../register/index';
  import { useActVm, useComponentInstance, useComponentType } from './composable';
  import { ERenderType } from './constants';
  import { setSvelteComponentReportData } from './utils/report';

  /**
   * 组件配置
   *
   * 这里只列出几个基础配置项
   */
  export let config: IComponentPropsConfig = {
    id: 0,
    type: '',
    name: '',
    style: {},
    dtEid: '',
    renderType: ERenderType.DEFAULT,
    report: {
      module: {},
    },
  };

  /**
   * 组件选项
   *
   * svelte 组件由 vue 组件 ui/Component.js 包装
   * 这里直接从 Vue 组件获得一些已经计算好的必要属性，就不用再重复计算一次了
   * 上报相关的参数由于是用到了 Vue 指令 v-report，没办法直接从 ui/Component.js 获得，需要手动计算一次
   */
  export let componentOptions: {
    attrs: Record<string, any>;
    style: Record<string, any>;
    class: string;
  } = {
    attrs: {},
    style: {},
    class: '',
  };

  /**
   * 组件真正要挂载的节点
   */
  export let target: HTMLElement;

  /**
   * 真正的 svelte 组件实例
   */
  let componentInstance: SvelteComponent<any, any, any>;
  export const getComponentInstance = () => componentInstance;

  /**
   * 真正的 svelte 组件对应的 dom 节点
   */
  let componentElement: HTMLElement;
  export const getComponentElement = () => componentElement;

  /**
   * 真正组件基础类型:
   * - container 容器
   * - pop 弹窗
   * - component 普通组件
   */
  let componentBaseType: string = 'component';
  export const getComponentBaseType = () => componentBaseType;

  /**
   * 获取组件类型
   */
  export const getComponentType = () => config.type;

  let id = '';

  const dispatch = createEventDispatcher();
  const { setComponentInstance } = useComponentInstance(config);
  const { componentType } = useComponentType({ config });

  $: {
    if (/-pop/i.test(config.type)) {
      componentBaseType = 'pop';
    } else if (config.type === 'container') {
      componentBaseType = 'container';
    }

    id = `${config.id}`;
  }

  const context = getAllContexts();
  const actVm = useActVm();
  const emit = context.get('emit') as Function;

  // 等待组件 Mounted 之后，需要将 id、style、class、大同上报等属性挂载到 svelte 组件的 element 上
  const onComponentMounted = (element: HTMLElement) => {
    const el = element;
    console.log('[sca] mounted', componentType);
    componentElement = el;
    const { attrs, style = {}, class: className } = componentOptions;
    el.setAttribute('id', attrs.id);

    if (className) {
      el.classList.add(...className.split(' '));
    }

    Object.keys(style).forEach((key) => {
      el.style[key as any] = style[key];
    });

    setSvelteComponentReportData(config, el);
  };

  export const updateComponentStyle = (style: Record<string, any>) => {
    if (componentElement) {
      Object.keys(style).forEach((key) => {
        componentElement.style[key as any] = style[key];
      });
    }
  };

  window.reportAegisTime?.({
    name: 'svelte:resolveStart',
    ext1: config.type,
    ext2: config.id,
    ext3: window.actId,
  });
  console.log('[sca] resolve', new Date().getTime(), componentType, 'config:', config);
  resolveSvelteComponent(componentType).then((ComponentConstructor) => {
    console.log('[sca] new', new Date().getTime(), componentType);
    window.reportAegisTime?.({
      name: 'svelte:resolveEnd',
      ext1: config.type,
      ext2: id,
      ext3: window.actId,
    });

    componentInstance = new (ComponentConstructor as any)({
      props: {
        config,
      },
      target,
      context,
    });

    const { created, mounted } = config;
    const eventName = `component.${id}.init`;

    if (isFunction(created)) {
      Promise
        .resolve(created.bind(null)(actVm, config))
        .then(() => {
          emit(eventName);
        });
    }

    if (target.children.length > 0) {
      onComponentMounted(target.children[0] as HTMLElement);
      mounted?.bind(null)(actVm, config);
    } else {
      const onObserver = (_: any, observer: MutationObserver) => {
        observer.disconnect();
        onComponentMounted(target.children[0] as HTMLElement);
        mounted?.bind(null)(actVm, config);
      };
      const observer = new MutationObserver(onObserver);
      observer.observe(target, {
        childList: true,
      });
    }

    // 标记为 svelte 组件，以便在添加到全局组件数组中可以区分处理
    componentInstance.isSvelte = true;
    setComponentInstance(componentInstance);
    dispatch(events.componentCreated);
    window.reportAegisTime?.({
      name: 'svelte:finished',
      ext1: config.type,
      ext2: id,
      ext3: window.actId,
    });
  })
    .catch((e) => {
      console.error('[sca] resolve or new comp error', componentType, 'error:', e);
      window.reportAegisTime?.({
        name: 'svelte:error',
        ext1: config.type,
        ext2: id,
        ext3: window.actId,
      });
    });

</script>
